// FormModal.tsx - Updated with improved design from second version
import React, { useState, useCallback } from 'react';
import CompanySetupForm from './forms/CompanySetupForm';
import SurveyingForm from './forms/SurveyingForm';
import RemovalsForm from './forms/RemovalsForm';
import AdditionalProductForm from './forms/AdditionalProductForm';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formType: string;
  serviceName: string;
  onFormComplete: (formData: any, requiresPayment?: boolean) => void;
  onPaymentComplete?: (paymentData: any) => void;
  onPaymentFailed?: (error: any, failedFormData: any) => void;
  themes: any;
  initialFormData?: any;
  paymentStatus?: 'idle' | 'processing' | 'success' | 'failed';
  productId?: string; // Add productId prop for AdditionalProductForm
}

const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  formType,
  serviceName,
  onFormComplete,
  onPaymentComplete,
  onPaymentFailed,
  themes,
  initialFormData,
  paymentStatus: initialPaymentStatus = 'idle',
  productId = ''
}) => {
  // State management
  const [formData, setFormData] = useState<any>(initialFormData || null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>(initialPaymentStatus);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Utility function to check for white or near-white shades
  const isWhiteOrShade = (color: string) => {
    if (!color) return false;
    if (color?.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  // Database save (always happens)
  const saveFormToDatabase = useCallback(async (data: any) => {
    let payload;
    
    if (formType === 'AdditionalProductForm') {
      // For additional products, include productId and service details
      payload = {
        formType: formType,
        formData: JSON.stringify(data),
        productId: productId,
        serviceName: serviceName,
        status: 'submitted',
        timestamp: new Date().toISOString(),
        websiteName: themes?.name,
        websiteURL: themes?.website
      };
    } else {
      // For existing forms (CompanySetup, Surveying, Removals)
      payload = {
        formType,
        formData: JSON.stringify(data),
        status: 'submitted',
        timestamp: new Date().toISOString(),
        websiteName: themes?.name,
        websiteURL: themes?.website
      };
    }

    const response = await fetch('/api/whitelabel/forms/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to save form data' }));
      throw new Error(errorData.message || 'Failed to save form data');
    }

    return response.json();
  }, [formType, productId, serviceName, themes]);

  // Calculate payment amount based on form data
  const calculatePaymentAmount = useCallback((data: any): number => {
    // For CompanySetupForm, use service level pricing
    if (data.serviceLevel) {
      const pricing: Record<string, number> = {
        basic: 50,
        standard: 150,
        premium: 250
      };
      return pricing[data.serviceLevel as keyof typeof pricing] || 50;
    }
    
    // For other forms, use default pricing
    return 50;
  }, []);

  // Payment processing
  const handlePaymentFlow = useCallback(async (data: any) => {
    setPaymentStatus('processing');
    
    try {
      // Calculate total based on form data
      const total = calculatePaymentAmount(data);
      
      // Create Stripe session
      const stripeResponse = await fetch('/api/payments/create-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total * 100, // Convert to pence
          formData: data,
          formType,
          productId,
          serviceName,
          successUrl: `${window.location.origin}/payment-success`,
          cancelUrl: `${window.location.origin}/payment-cancelled`
        })
      });

      if (!stripeResponse.ok) {
        throw new Error('Failed to create payment session');
      }

      const { sessionUrl } = await stripeResponse.json();
      
      // Redirect to Stripe
      window.location.href = sessionUrl;
      
    } catch (error: any) {
      setPaymentStatus('failed');
      setPaymentError(error.message);
      throw error;
    }
  }, [calculatePaymentAmount, formType, productId, serviceName]);

  // Error handler - keeps form open with data preserved
  const handleFormError = useCallback((error: any, data: any) => {
    console.error('❌ Form error:', error);
    setPaymentStatus('failed');
    
    // Parse API errors into form field errors
    if (error.fieldErrors) {
      setFormErrors(error.fieldErrors);
    } else {
      setPaymentError(error.message || 'Something went wrong');
    }
    
    // Keep form data so user can retry
    setFormData(data);
  }, []);

  // Clean close handler
  const handleClose = useCallback(() => {
    setFormData(null);
    setFormErrors({});
    setIsSubmitting(false);
    setPaymentStatus('idle');
    setPaymentError(null);
    onClose();
  }, [onClose]);

  // Success handler
  const handleFormSuccess = useCallback((data: any) => {
    console.log('✅ Form completed successfully:', data);
    setPaymentStatus('success');
    onFormComplete(data, false);
    
    // Auto-close after success message
    setTimeout(() => {
      handleClose();
    }, 3000);
  }, [onFormComplete, handleClose]);

  // Form submission handler
  const handleFormSubmit = useCallback(async (data: any, requiresPayment: boolean = false) => {
    console.log('📝 Form submitted:', { 
      formType, 
      productId, 
      serviceName, 
      data, 
      requiresPayment 
    });
    
    setFormData(data);
    setIsSubmitting(true);
    setFormErrors({});

    try {
      // Save to database first (always)
      await saveFormToDatabase(data);
      
      if (requiresPayment) {
        // Process payment
        await handlePaymentFlow(data);
      } else {
        // No payment needed - complete immediately
        handleFormSuccess(data);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      handleFormError(error, data);
    } finally {
      setIsSubmitting(false);
    }
  }, [formType, productId, serviceName, saveFormToDatabase, handlePaymentFlow, handleFormSuccess, handleFormError]);

  // Payment retry handler
  const handlePaymentRetry = useCallback(async () => {
    if (!formData) return;
    
    setPaymentError(null);
    setPaymentStatus('idle');
    
    try {
      await handlePaymentFlow(formData);
    } catch (error: any) {
      setPaymentStatus('failed');
      setPaymentError(error.message);
    }
  }, [formData, handlePaymentFlow]);

  // Cancel handler - warns if data exists
  const handleCancel = useCallback(() => {
    if (formData && Object.keys(formData).length > 0) {
      const shouldClose = window.confirm('Are you sure? Your progress will be lost.');
      if (shouldClose) {
        handleClose();
      }
    } else {
      handleClose();
    }
  }, [formData, handleClose]);

  // Render form based on type
  const renderForm = () => {
    const baseProps = {
      initialData: formData,
      errors: formErrors,
      isSubmitting,
      onSubmit: handleFormSubmit,
      onCancel: handleCancel,
      themes
    };

    switch (formType) {
      case 'CompanySetupForm':
        return <CompanySetupForm {...baseProps} />;
      
      case 'SurveyingForm':
        return <SurveyingForm {...baseProps} />;
      
      case 'RemovalsForm':
        return <RemovalsForm {...baseProps} />;

      case 'AdditionalProductForm':
        return (
          <AdditionalProductForm 
            {...baseProps}
            productId={productId}
            serviceName={serviceName}
          />
        );

      case 'FurnishingForm':
        return renderComingSoonForm();
        
      default:
        return renderComingSoonForm();
    }
  };

  // Coming soon placeholder
  const renderComingSoonForm = () => (
    <div className="p-8 text-center space-y-4">
      <div className="text-6xl mb-4">🚧</div>
      <h3 className="text-xl font-bold">Form Coming Soon</h3>
      <p className="text-gray-600 max-w-md mx-auto">
        The {serviceName} form is currently being developed. 
        Please contact us directly for now.
      </p>
      <div className="flex gap-4 justify-center mt-6">
        <button
          onClick={handleClose}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Close
        </button>
        <a
          href="mailto:info@company.com"
          className="px-6 py-2 text-white rounded-lg"
          style={{ backgroundColor: themes?.color1 || '#1E5188' }}
        >
          Contact Us
        </a>
      </div>
    </div>
  );

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-[9999]" 
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && paymentStatus !== 'processing') {
          handleCancel();
        }
      }}
    >
      <div 
        className="rounded-lg flex flex-col shadow-xl relative" 
        style={{
          width: 'min(95vw, 900px)',
          height: 'min(90vh, 700px)',
          maxWidth: '900px',
          maxHeight: '700px',
          backgroundColor: 'white',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex justify-between items-center p-4 rounded-t-lg shrink-0"
          style={{ 
            backgroundColor: paymentStatus === 'success' ? '#16a34a' : 
                           paymentStatus === 'failed' ? '#dc2626' :
                           (themes?.color1 || '#1E5188'),
            color: isWhiteOrShade(themes?.color1) ? "#000000" : "#ffffff"
          }}
        >
          <h3 className="text-lg md:text-xl font-bold truncate pr-4">
            {paymentStatus === 'success' ? '✅ Success!' :
             paymentStatus === 'failed' ? '❌ Payment Failed' :
             paymentStatus === 'processing' ? '💳 Processing Payment...' :
             `${serviceName} - Service Request`}
          </h3>
          {paymentStatus !== 'processing' && (
            <button 
              onClick={handleCancel}
              className="text-2xl font-bold transition-transform duration-200 hover:scale-125 shrink-0"
              style={{ 
                color: isWhiteOrShade(themes?.color1) ? "#000000" : "#ffffff"
              }}
              title="Close form"
            >
              &times;
            </button>
          )}
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto min-h-0 p-0">
          {paymentStatus === 'processing' ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
                <h3 className="text-xl font-semibold">Processing Payment...</h3>
                <p className="text-gray-600">Please wait while we redirect you to Stripe</p>
                <p className="text-sm text-gray-500">Do not close this window</p>
              </div>
            </div>
          ) : paymentStatus === 'success' ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4 p-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-green-600">Payment Successful!</h3>
                <p className="text-gray-600 max-w-md">
                  Your {serviceName} request has been submitted and payment confirmed. 
                  Our team will be in touch shortly.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleClose}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    Complete
                  </button>
                </div>
              </div>
            </div>
          ) : paymentStatus === 'failed' && paymentError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4 p-8">
                <div className="text-6xl mb-4">❌</div>
                <h3 className="text-2xl font-bold text-red-600">Payment Failed</h3>
                <p className="text-gray-600 max-w-md mb-4">
                  {paymentError}
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Your form data has been saved. You can try payment again or contact us directly.
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={handlePaymentRetry}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    Try Payment Again
                  </button>
                  <button
                    onClick={handleClose}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
                  >
                    Contact Us Instead
                  </button>
                </div>
              </div>
            </div>
          ) : (
            renderForm()
          )}
        </div>
      </div>
    </div>
  );
};

export default FormModal;